import { typographyTokens } from '../../../../theme/typographyTokens';
import React, {useState, useRef, useEffect} from "react";
import {
  DialogActions,
  DialogContent,
  Button,
  TextField,
  Typography,
  Box,
  useTheme,
  Select,
  MenuItem,
  FormControl,
  Chip,
  ThemeProvider,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { styled } from '@mui/system';
import CloseIcon from '@mui/icons-material/Close';
import { emailNotificationsType } from "../../../../utils/emailNotificationTypes";
import UserSearchBar from "../../../../components/TaskOverview/AutoComplete";

type EmailNotification = {
  id: string;
  subject: string;
  body: string;
  toRecipients: string[];
  ccRecipients: string[];
  notificationNotes?: string;
};

type Props = {
  editing: EmailNotification | null;
  subject: string;
  notificationNotes?: string;
  body: string;
  toRecipients?: any;
  ccRecipients?: any;
  setSubject: (v: string) => void;
  setNotificationNotes: (v: string) => void;
  setBody: (v: string) => void;
  handleClose: () => void;
  handleSave: () => void;
  showUser: any;
  setShowUser: (v: any) => void;
  setEmailTemplate: (v: any) => void;
  setNotificationType: (v: string) => void;
  notificationType: string;
  showRecipients?: boolean;
  usedNotificationTypes?: string[];
  setToRecipients: (v: string) => void;
  multiSelectedToUsers: any[];
  setMultiSelectedToUsers: (v: any) => void;
  setCcRecipients: (v: string) => void;
  multiSelectedCcUsers: any[];
  setMultiSelectedCcUsers: (v: any) => void;
  addRequestorOfSEP: boolean;
  setAddRequestorOfSEP: (v: boolean) => void;
  addDepartmentStakeholder: boolean;
  setAddDepartmentStakeholder: (v: boolean) => void;
};

 interface StyledChipProps {
  onDelete: (tag: string) => void;
}

const StyledChip = styled(Chip)<StyledChipProps>(({ theme }) => ({
  margin: theme.spacing(0.5),
  transition: 'all 0.3s',
}));

const EmailNotificationDialog: React.FC<Props> = ({
  editing,
  subject,
  notificationNotes,
  body,
  toRecipients,
  ccRecipients,
  setSubject,
  setNotificationNotes,
  setBody,
  handleClose,
  handleSave,
  showUser,
  setShowUser,
  setEmailTemplate,
  setNotificationType,
  notificationType,
  showRecipients,
  usedNotificationTypes,
  setToRecipients,
  multiSelectedToUsers,
  setMultiSelectedToUsers,
  setCcRecipients,
  multiSelectedCcUsers,
  setMultiSelectedCcUsers,
  addRequestorOfSEP,
  setAddRequestorOfSEP,
  addDepartmentStakeholder,
  setAddDepartmentStakeholder,
}) => {
  const theme = useTheme();
  const [recipientOpen, setRecipientOpen] = useState(false);
  const [ccRecipientOpen, setCcRecipientOpen] = useState(false);
  
  const recipientAnchorEl = useRef(null);
  const ccrecipientAnchorEl = useRef(null);

  const onMultipleUserChange = (
    users: any,
    type: "toRecipients" | "ccRecipients"
  ) => {
    if (type === "toRecipients") {
    const newUser = { id: users.id, displayName: users.displayName || '' };
        const updatedUsers = [...multiSelectedToUsers, newUser];
        setMultiSelectedToUsers(updatedUsers);
        setToRecipients(updatedUsers.map(user => user.id).join(','));
    } else if (type === "ccRecipients") {
      const newUser = { id: users.id, displayName: users.displayName || '' };
        const updatedUsers = [...multiSelectedCcUsers, newUser];
        setMultiSelectedCcUsers(updatedUsers);
        setCcRecipients(updatedUsers.map(user => user.id).join(','));
    }
  };

  const handleDeleteTag = (id: string, type: "toRecipients" | "ccRecipients") => {
    if (type === "toRecipients") {
      const updatedUsers = multiSelectedToUsers?.filter(user => user.id !== id);
      setMultiSelectedToUsers(updatedUsers);
      setToRecipients(updatedUsers.map(user => user.id).join(','));
    } else if (type === "ccRecipients") {
      const updatedUsers = multiSelectedCcUsers?.filter(user => user.id !== id);
      setMultiSelectedCcUsers(updatedUsers);
      setCcRecipients(updatedUsers.map(user => user.id).join(','));
    }
  };

  const availableNotificationTypes = editing
    ? emailNotificationsType
    : emailNotificationsType.filter(
        (val) => !usedNotificationTypes?.includes(val.type)
      );

      useEffect(() => {
        if (toRecipients && toRecipients.length > 0) {
          if (JSON.stringify(multiSelectedToUsers) !== JSON.stringify(toRecipients)) {
            setMultiSelectedToUsers(toRecipients);
          }
        } else if (toRecipients === null || toRecipients === undefined) {
          if (multiSelectedToUsers.length !== 0) {
            setMultiSelectedToUsers([]);
          }
        }

        if (ccRecipients && ccRecipients.length > 0) {
          if (JSON.stringify(multiSelectedCcUsers) !== JSON.stringify(ccRecipients)) {
            setMultiSelectedCcUsers(ccRecipients);
          }
        } else if (ccRecipients === null || ccRecipients === undefined) {
          if (multiSelectedCcUsers.length !== 0) {
            setMultiSelectedCcUsers([]);
          }
        }
      // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [toRecipients, ccRecipients]);

  return (
    <>
      <DialogContent>
        <Typography variant="subtitle1" fontWeight={typographyTokens.fontWeights.semibold} gutterBottom>
          Select Notification Type
        </Typography>

        <FormControl fullWidth>
          <Select
            style={{ height: "40px" }}
            onChange={(e: any) => setNotificationType(e.target.value)}
            value={notificationType || ""}
          >
            {availableNotificationTypes.map((val) => (
              <MenuItem key={val.id} value={val.type}>
                {val.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Typography variant="subtitle1" fontWeight={typographyTokens.fontWeights.semibold} gutterBottom>
          Subject
        </Typography>
        <TextField
          label="Subject"
          fullWidth
          margin="normal"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          variant="outlined"
          style={{ marginTop: 2 }}
          InputProps={{
            sx: { borderRadius: 2, fontWeight: typographyTokens.fontWeights.medium },
          }}
        />
        <Typography variant="subtitle1" fontWeight={typographyTokens.fontWeights.semibold} gutterBottom>
          Body
        </Typography>
        <TextField
          label="Body"
          fullWidth
          margin="normal"
          multiline
          minRows={4}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          variant="outlined"
          style={{ marginTop: 2 }}
          InputProps={{
            sx: { borderRadius: 2, fontWeight: typographyTokens.fontWeights.medium },
          }}
        />
        <Box display="flex" flexDirection="row" mt={2}>
        <FormControlLabel
          control={
            <Checkbox
              checked={addRequestorOfSEP}
              onChange={(e) => setAddRequestorOfSEP(e.target.checked)}
            />
          }
          label="Add Requestor of SEP"
        />
        <FormControlLabel
            control={
              <Checkbox
                checked={addDepartmentStakeholder}
                onChange={(e) => setAddDepartmentStakeholder(e.target.checked)}
              />
            }
            label="Add Department Stakeholder"
          />
          </Box>
        <Box mt={2}>
          <Typography variant="subtitle1" fontWeight={typographyTokens.fontWeights.semibold}>
            To Recipients
          </Typography>
          <ThemeProvider theme={theme}>
            <Box
              ref={recipientAnchorEl}
              role="region"
              aria-label="Tag Input System"
              onClick={(e) => {
                setRecipientOpen(!recipientOpen);
                // setRecipientAnchorEl(e.currentTarget);
              }}
              sx={{
                border: "1px solid",
                padding: "5px",
                cursor: "pointer",
                borderRadius: "4px",
                minHeight: "40px",
              }}
            >
              {multiSelectedToUsers?.map(
                ({ id, displayName }: { id: string; displayName: string }) => (
                  <StyledChip
                    key={id}
                    label={displayName}
                    onDelete={() => handleDeleteTag(id, "toRecipients")}
                    deleteIcon={<CloseIcon />}
                  />
                )
              )}
            </Box>
          </ThemeProvider>
          <UserSearchBar
            show={recipientOpen}
            type="knockoutScreen"
            anchor={recipientAnchorEl.current}
            showConfirmButton={false}
            onClose={() => setRecipientOpen(false)}
            onChange={(users: any) =>
              onMultipleUserChange(users, "toRecipients")
            }
          />
        </Box>
        <Box mt={2}>
          <Typography variant="subtitle1" fontWeight={typographyTokens.fontWeights.semibold}>
            CC Recipients
          </Typography>
          <ThemeProvider theme={theme}>
<Box
              ref={ccrecipientAnchorEl}
              role="region"
              aria-label="Tag Input System"
              onClick={(e) => {
                setCcRecipientOpen(!ccRecipientOpen);
              }}
              sx={{
                border: "1px solid",
                padding: "5px",
                cursor: "pointer",
                borderRadius: "4px",
                minHeight: "40px",
              }}
            >
              {multiSelectedCcUsers?.map(
                ({ id, displayName }: { id: string; displayName: string }) => (
                  <StyledChip
                    key={id}
                    label={displayName}
                    onDelete={() => handleDeleteTag(id, "ccRecipients")}
                    deleteIcon={<CloseIcon />}
                  />
                )
              )}

            </Box>


          </ThemeProvider>
          <UserSearchBar
            show={ccRecipientOpen}
            type="knockoutScreen"
            anchor={ccrecipientAnchorEl.current}
            showConfirmButton={false}
            onClose={() => setCcRecipientOpen(false)}
            onChange={(users: any) =>
              onMultipleUserChange(users, "ccRecipients")
            }
          />          
        </Box> 
        <Typography variant="subtitle1" fontWeight={typographyTokens.fontWeights.semibold} gutterBottom mt={2}>
          Notification Notes
        </Typography>
        <TextField
          label="Notification Notes"
          fullWidth
          multiline
          rows={4}
          margin="normal"
          value={notificationNotes}
          onChange={(e) => setNotificationNotes(e.target.value)}
          variant="outlined"
          style={{ marginTop: 2 }}
          InputProps={{
            sx: { borderRadius: 2, fontWeight: typographyTokens.fontWeights.medium },
          }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleClose}
          sx={{
            borderRadius: 3,
            fontWeight: typographyTokens.fontWeights.semibold,
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          sx={{
            borderRadius: 3,
            fontWeight: typographyTokens.fontWeights.semibold,
            color: "#fff",
            px: 4,
            boxShadow: 2,
          }}
        >
          {editing ? "Update" : "Create"}
        </Button>
      </DialogActions>
    </>
  );
};

export default EmailNotificationDialog;
